// =====================================
// PUBLIC HELPER FUNCTIONS
// =====================================
/**
 * Checks if variable data is event list data (generated by Axon's processBaseResponse)
 * Based on the actual structure: [{ value: taskResponseId, labels: {...} }]
 */
export const isEventListData = (data) => {
    return (Array.isArray(data) &&
        data.length > 0 &&
        typeof data[0] === 'object' &&
        'value' in data[0] &&
        'labels' in data[0] &&
        typeof data[0].labels === 'object');
};
/**
 * Renders event list variable display text
 * Adapts the display logic from Axon's processBaseResponse
 */
export const renderEventListDisplay = (eventListData) => {
    const displayItems = [];
    eventListData.forEach((listItem) => {
        if (listItem.labels) {
            const labelDisplays = Object.values(listItem.labels)
                .filter((label) => label && label.display)
                .map((label) => label.display);
            if (labelDisplays.length > 0) {
                displayItems.push(labelDisplays.join(', '));
            }
        }
        // Handle sub-lists directly (no recursion - subList items don't have their own subList)
        if (listItem.subList && listItem.subList.length > 0) {
            listItem.subList.forEach((subItem) => {
                if (subItem.labels) {
                    const subLabelDisplays = Object.values(subItem.labels)
                        .filter((label) => label && label.display)
                        .map((label) => label.display);
                    if (subLabelDisplays.length > 0) {
                        displayItems.push(`  ${subLabelDisplays.join(', ')}`); // Indent sub-items
                    }
                }
            });
        }
    });
    return displayItems.join('\n');
};
/**
 * Checks if any variables in metadata contain event list variables
 */
export const hasEventListVariables = (variables) => {
    if (!variables) {
        return false;
    }
    return Object.values(variables).some((variableData) => isEventListData(variableData));
};
/**
 * Returns display text for a single variable entry (used for {varName} substitution).
 * Handles: event list (renderEventListDisplay), array of { display }, or single { display }.
 */
export function getVariableDisplayText(variableData) {
    if (variableData == null) {
        return undefined;
    }
    if (isEventListData(variableData)) {
        return renderEventListDisplay(variableData);
    }
    if (Array.isArray(variableData)) {
        const displays = variableData.map((x) => x?.display).filter(Boolean);
        return displays.length ? displays.join(', ') : undefined;
    }
    if (typeof variableData === 'object' && 'display' in variableData) {
        return variableData.display;
    }
    return undefined;
}
/**
 * Replaces {word} placeholders in a string with the variable's display text.
 * Unknown keys or missing variables are left as {key} so consumers (e.g. LLM) still see the original form.
 */
export function substituteVariablesInString(str, variables) {
    if (!variables || typeof str !== 'string') {
        return str;
    }
    return str.replace(/\{(\w+)\}/g, (_, key) => getVariableDisplayText(variables[key]) ?? `{${key}}`);
}
const TEXT_STEP_KEYS = ['c_description', 'c_question', 'c_text', 'c_text_choices'];
/**
 * Applies variable substitution to step text fields (c_question, c_description, c_text, c_text_choices).
 * Used by ResponseDetailView and VisitActivityResponses to resolve {variable} placeholders.
 * Only substitutes simple variables ({ display: string }) — array-shaped and event-list variables
 * are intentionally excluded, matching the original behaviour in the components.
 */
export function applyVariablesToStepResponse(stepResponse, variables) {
    if (!variables) {
        return stepResponse;
    }
    // Restrict to simple { display } variables only, matching original component behaviour
    const simpleVariables = Object.fromEntries(Object.entries(variables).filter(([, v]) => v != null && !Array.isArray(v) && 'display' in v));
    let processedStep = { ...stepResponse.c_step };
    for (const stepKey of TEXT_STEP_KEYS) {
        const stepValue = processedStep[stepKey];
        if (stepValue === undefined) {
            continue;
        }
        if (Array.isArray(stepValue)) {
            processedStep = {
                ...processedStep,
                [stepKey]: stepValue.map((item) => item?.c_text ? { ...item, c_text: substituteVariablesInString(item.c_text, simpleVariables) } : item),
            };
        }
        else if (typeof stepValue === 'string') {
            processedStep = { ...processedStep, [stepKey]: substituteVariablesInString(stepValue, simpleVariables) };
        }
    }
    substituteVariablesInEventListData(variables);
    return { ...stepResponse, c_step: processedStep };
}
/**
 * Generates text choices from event list variable data
 * Adapts logic from Axon's generateStepLabels function
 */
const generateTextChoicesFromEventList = (eventListData) => {
    const textChoices = [];
    let order = 1;
    eventListData.forEach((listItem) => {
        if (listItem.labels) {
            Object.entries(listItem.labels).forEach(([labelKey, labelData]) => {
                if (labelData.display) {
                    textChoices.push({
                        c_key: labelKey,
                        c_order: order++,
                        c_text: labelData.display,
                        c_value: labelData.value?.toString() || '',
                    });
                }
            });
        }
        if (listItem.subList && listItem.subList.length > 0) {
            listItem.subList.forEach((subItem) => {
                if (subItem.labels) {
                    Object.entries(subItem.labels).forEach(([labelKey, labelData]) => {
                        if (labelData.display) {
                            textChoices.push({
                                c_key: labelKey,
                                c_order: order++,
                                c_text: labelData.display,
                                c_value: labelData.value?.toString() || '',
                            });
                        }
                    });
                }
            });
        }
    });
    return textChoices;
};
export const processStepResponseWithEventList = (stepResponse, variables // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
    const processedResponse = { ...stepResponse };
    if (!variables) {
        return processedResponse;
    }
    const choicesVariable = processedResponse.c_step.c_screen_details?.c_screen_data?.choices_variable;
    const choicesVariableData = choicesVariable && variables[choicesVariable];
    if ((!processedResponse.c_step.c_text_choices || !processedResponse.c_step.c_text_choices.length) &&
        choicesVariableData &&
        isEventListData(choicesVariableData)) {
        const dynamicChoices = generateTextChoicesFromEventList(choicesVariableData);
        if (dynamicChoices.length > 0) {
            processedResponse.c_step = {
                ...processedResponse.c_step,
                c_text_choices: dynamicChoices,
            };
        }
    }
    return processedResponse;
};
/**
 * Resolves task response IDs to event list labels for display
 * Used when step response c_value contains IDs that reference event list items
 */
export const resolveEventListLabels = (responseValues, variables) => {
    if (!variables || !Array.isArray(responseValues)) {
        return '';
    }
    const resolvedLabels = [];
    for (const variableData of Object.values(variables)) {
        if (isEventListData(variableData)) {
            for (const responseValue of responseValues) {
                const matchingItem = variableData.find((item) => item.value === responseValue);
                if (matchingItem && matchingItem.labels) {
                    const labelDisplays = Object.values(matchingItem.labels)
                        .filter((label) => label && typeof label.display === 'string')
                        .map((label) => label.display);
                    if (labelDisplays.length > 0) {
                        resolvedLabels.push(labelDisplays.join(', '));
                    }
                }
            }
        }
    }
    return resolvedLabels.join(', ');
};
/**
 * Applies variable substitution to display values within event list variables
 * Self-contained - handles substitution internally without needing a callback
 */
export const substituteVariablesInEventListData = (variables) => {
    for (const variable in variables) {
        const variableData = variables[variable];
        if (isEventListData(variableData)) {
            variableData.forEach((listItem) => {
                if (listItem.labels) {
                    Object.values(listItem.labels).forEach((label) => {
                        if (label && typeof label.display === 'string') {
                            for (const simpleVariable in variables) {
                                const simpleVariableData = variables[simpleVariable];
                                if (simpleVariableData && !Array.isArray(simpleVariableData) && 'display' in simpleVariableData) {
                                    if (label.display.includes(`{${simpleVariable}}`)) {
                                        label.display = label.display.replace(new RegExp(`{${simpleVariable}}`, 'g'), simpleVariableData.display || '');
                                    }
                                }
                            }
                        }
                    });
                }
                if (listItem.subList) {
                    listItem.subList.forEach((subItem) => {
                        if (subItem.labels) {
                            Object.values(subItem.labels).forEach((label) => {
                                if (label && typeof label.display === 'string') {
                                    for (const simpleVariable in variables) {
                                        const simpleVariableData = variables[simpleVariable];
                                        if (simpleVariableData && !Array.isArray(simpleVariableData) && 'display' in simpleVariableData) {
                                            if (label.display.includes(`{${simpleVariable}}`)) {
                                                label.display = label.display.replace(new RegExp(`{${simpleVariable}}`, 'g'), simpleVariableData.display || '');
                                            }
                                        }
                                    }
                                }
                            });
                        }
                    });
                }
            });
        }
    }
};
